export const GRADE_PUSH_CONCURRENCY = 8;
export const PDF_PUSH_CONCURRENCY = 2;

// Separate limits let network requests overlap without loading every PDF into memory.
export const createTaskLimiter = (concurrency) => {
    const queue = [];
    let active = 0;
    const runNext = () => {
        if (active >= concurrency || !queue.length) return;
        const { operation, resolve, reject } = queue.shift();
        active += 1;
        Promise.resolve()
            .then(operation)
            .then(resolve, reject)
            .finally(() => {
                active -= 1;
                runNext();
            });
    };
    return (operation) =>
        new Promise((resolve, reject) => {
            queue.push({ operation, resolve, reject });
            runNext();
        });
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_RETRIES = 5;
const MAX_BACKOFF_MS = 10000;

const getRetryDelay = (response, attempt) => {
    const retryAfter = response.headers?.['retry-after'] ?? response.data?.retryAfter;
    const seconds = retryAfter == null || retryAfter === '' ? NaN : Number(retryAfter);
    const requestedDelay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - Date.now();
    const backoff = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
    return Math.max(backoff, Number.isFinite(requestedDelay) ? requestedDelay : 0) + Math.floor(Math.random() * 250);
};

// Share the cooldown across a push run so other workers do not keep feeding a throttled LMS.
export const createGradePushRequest = (request) => {
    let retryAt = 0;
    return async (body) => {
        for (let attempt = 0; ; attempt += 1) {
            while (retryAt > Date.now()) await delay(retryAt - Date.now());
            try {
                return await request(body);
            } catch (error) {
                // The app interceptor rejects the response directly; also accept native Axios errors.
                const response = error?.response ?? error;
                const status = Number(response?.status);
                const rateLimited =
                    status === 429 || (status === 403 && /rate.?limit/i.test(JSON.stringify(response?.data ?? '')));
                if (!rateLimited || response?.data?.retryable === false) throw error;
                retryAt = Math.max(retryAt, Date.now() + getRetryDelay(response, attempt));
                if (attempt >= MAX_RETRIES) throw error;
            }
        }
    };
};
